import { blessnetMainnet, blessnetSepolia } from "@/config/bless-chain";
import { isMainnet } from "@/constants/flags.constant";
import type { WagmiConfigType } from "@/providers/crypto.provider";
import {
  arbitrum,
  arbitrumSepolia,
  base,
  baseSepolia,
  mainnet,
  optimism,
  optimismSepolia,
  sepolia,
  type Chain,
} from "wagmi/chains";

const CHAIN_SETS: Record<
  WagmiConfigType,
  {
    mainnet: [Chain, ...Chain[]];
    testnet: [Chain, ...Chain[]];
  }
> = {
  all_chains: {
    mainnet: [blessnetMainnet, mainnet, arbitrum, optimism, base],
    testnet: [
      blessnetSepolia,
      sepolia,
      arbitrumSepolia,
      optimismSepolia,
      baseSepolia,
    ],
  },
  dispenser: {
    mainnet: [blessnetMainnet],
    testnet: [arbitrumSepolia, sepolia],
  },
};

/**
 * Retrieves a set of blockchain chains based on the provided configuration type.
 *
 * @param configType - The type of configuration used to determine the set of chains.
 *                      This should be a value of type `WagmiConfigType`.
 * @returns A tuple containing one or more `Chain` objects, representing the selected chains.
 *          The selection is based on whether the environment is mainnet or testnet.
 * @throws {Error} If the provided `configType` is unsupported or does not map to any chains.
 */
export const getChainsByConfigType = (
  configType: WagmiConfigType,
): [Chain, ...Chain[]] => {
  const chains = CHAIN_SETS[configType];
  const selectedChains = isMainnet ? chains.mainnet : chains.testnet;

  if (!selectedChains) {
    throw new Error(`Unsupported config type: ${configType}`);
  }

  return selectedChains;
};
